import { Toast as ToastPrimitive } from '@base-ui/react/toast'
import {
  Alert02Icon,
  Cancel01Icon,
  CheckmarkCircle02Icon,
  InformationCircleIcon,
  MultiplicationSignCircleIcon,
} from '@hugeicons/core-free-icons'
import { HugeiconsIcon } from '@hugeicons/react'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'

const toast = ToastPrimitive.createToastManager()

export type ToastVariant = 'default' | 'success' | 'error' | 'warning' | 'info'

export type ToastViewportPosition =
  'bottom-right' | 'bottom-center' | 'bottom-left' | 'top-right' | 'top-center' | 'top-left'

const ToastPositionContext = React.createContext<ToastViewportPosition>('bottom-right')

function toastAnchorFromPosition(position: ToastViewportPosition): 'top' | 'bottom' {
  return position.startsWith('top') ? 'top' : 'bottom'
}

function swipeDirectionsForPosition(
  position: ToastViewportPosition
): Array<'up' | 'down' | 'left' | 'right'> {
  return position.startsWith('top') ? ['up', 'right'] : ['down', 'right']
}

const toastVariants = cva(
  [
    'group/toast pointer-events-auto absolute right-0 z-[calc(1000-var(--toast-index))] w-full rounded-2xl border will-change-transform outline-none select-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
    '[--gap:0.75rem] [--height:var(--toast-frontmost-height,var(--toast-height))] [--peek:0.75rem] [--scale:calc(max(0,1-(var(--toast-index)*0.1)))] [--shrink:calc(1-var(--scale))]',
    'data-expanded:h-(--toast-height) data-expanded:[transform:translateX(var(--toast-swipe-movement-x))_translateY(var(--offset-y))]',
    'data-limited:opacity-0',
    'data-ending-style:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]',
    'data-ending-style:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]',
    'data-ending-style:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]',
    'data-ending-style:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]',
    'data-expanded:data-ending-style:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]',
    'data-expanded:data-ending-style:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]',
    'data-expanded:data-ending-style:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]',
    'data-expanded:data-ending-style:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]',
  ],
  {
    variants: {
      anchor: {
        bottom: [
          'bottom-0 origin-bottom',
          '[--offset-y:calc(var(--toast-offset-y)*-1+calc(var(--toast-index)*var(--gap)*-1)+var(--toast-swipe-movement-y))]',
          'h-(--height) [transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--peek))-(var(--shrink)*var(--height))))_scale(var(--scale))] [transition:transform_500ms_cubic-bezier(0.22,1,0.36,1),opacity_500ms,height_150ms]',
          "after:absolute after:top-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-['']",
          'data-starting-style:[transform:translateY(150%)]',
          '[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(150%)]',
        ],
        top: [
          'top-0 origin-top',
          '[--offset-y:calc(var(--toast-offset-y)+(var(--toast-index)*var(--gap))+var(--toast-swipe-movement-y))]',
          'h-(--height) [transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)+(var(--toast-index)*var(--peek))+(var(--shrink)*var(--height))))_scale(var(--scale))] [transition:transform_500ms_cubic-bezier(0.22,1,0.36,1),opacity_500ms,height_150ms]',
          "after:absolute after:bottom-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-['']",
          'data-starting-style:[transform:translateY(-150%)]',
          '[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(-150%)]',
        ],
      },
      variant: {
        default:
          'border-border bg-card text-card-foreground *:data-[slot=toast-description]:text-muted-foreground',
        success:
          'border-success/30 bg-[color-mix(in_oklab,var(--success)_10%,var(--background))] text-success *:data-[slot=toast-description]:text-success/90 *:[svg]:text-success',
        error:
          'border-error/30 bg-[color-mix(in_oklab,var(--error)_10%,var(--background))] text-error *:data-[slot=toast-description]:text-error/90 *:[svg]:text-error',
        warning:
          'border-warning/30 bg-[color-mix(in_oklab,var(--warning)_10%,var(--background))] text-warning *:data-[slot=toast-description]:text-warning/90 *:[svg]:text-warning',
        info: 'border-info/30 bg-[color-mix(in_oklab,var(--info)_10%,var(--background))] text-info *:data-[slot=toast-description]:text-info/90 *:[svg]:text-info',
      },
    },
    defaultVariants: {
      anchor: 'bottom',
      variant: 'default',
    },
  }
)

const toastViewportVariants = cva(
  'pointer-events-none fixed z-50 w-full max-w-[min(24rem,calc(100vw-3rem))] outline-none',
  {
    variants: {
      position: {
        'bottom-right': 'right-6 bottom-6 left-auto',
        'bottom-center': 'inset-x-6 bottom-6 mx-auto',
        'bottom-left': 'right-auto bottom-6 left-6',
        'top-right': 'top-6 right-6 left-auto',
        'top-center': 'inset-x-6 top-6 mx-auto',
        'top-left': 'top-6 right-auto left-6',
      },
    },
    defaultVariants: {
      position: 'bottom-right',
    },
  }
)

export function resolveToastVariant(type?: string): ToastVariant {
  if (type === 'success' || type === 'error' || type === 'warning' || type === 'info') {
    return type
  }
  return 'default'
}

function ToastProvider({ ...props }: ToastPrimitive.Provider.Props) {
  return <ToastPrimitive.Provider {...props} />
}

function ToastPortal({ ...props }: ToastPrimitive.Portal.Props) {
  return <ToastPrimitive.Portal data-slot="toast-portal" {...props} />
}

function ToastViewport({
  className,
  position = 'bottom-right',
  ...props
}: ToastPrimitive.Viewport.Props & {
  position?: ToastViewportPosition
}) {
  return (
    <ToastPrimitive.Viewport
      data-slot="toast-viewport"
      className={cn(toastViewportVariants({ position }), className)}
      {...props}
    />
  )
}

function Toast({
  className,
  variant,
  anchor,
  toast: toastObject,
  ...props
}: ToastPrimitive.Root.Props & VariantProps<typeof toastVariants>) {
  const viewportPosition = React.useContext(ToastPositionContext)
  const resolvedVariant = variant ?? resolveToastVariant(toastObject?.type)
  const resolvedAnchor = anchor ?? toastAnchorFromPosition(viewportPosition)

  return (
    <ToastPrimitive.Root
      data-slot="toast"
      data-variant={resolvedVariant}
      className={cn(toastVariants({ variant: resolvedVariant, anchor: resolvedAnchor }), className)}
      toast={toastObject}
      {...props}
    />
  )
}

function ToastContent({ className, ...props }: ToastPrimitive.Content.Props) {
  return (
    <ToastPrimitive.Content
      data-slot="toast-content"
      className={cn(
        'flex h-full items-center gap-3 overflow-hidden p-4 transition-opacity duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] data-behind:opacity-0 data-expanded:opacity-100',
        className
      )}
      {...props}
    />
  )
}

function ToastTitle({ className, ...props }: ToastPrimitive.Title.Props) {
  return (
    <ToastPrimitive.Title
      data-slot="toast-title"
      className={cn('text-sm font-medium', className)}
      {...props}
    />
  )
}

function ToastDescription({ className, ...props }: ToastPrimitive.Description.Props) {
  return (
    <ToastPrimitive.Description
      data-slot="toast-description"
      className={cn('text-sm', className)}
      {...props}
    />
  )
}

function ToastAction({
  className,
  render = <Button variant="outline" size="sm" />,
  ...props
}: ToastPrimitive.Action.Props) {
  return (
    <ToastPrimitive.Action
      data-slot="toast-action"
      render={render}
      className={cn('shrink-0', className)}
      {...props}
    />
  )
}

function ToastClose({
  className,
  children,
  render = <Button variant="ghost" size="icon-sm" />,
  ...props
}: ToastPrimitive.Close.Props) {
  return (
    <ToastPrimitive.Close
      data-slot="toast-close"
      aria-label="Fechar notificação"
      render={render}
      className={cn(
        "relative shrink-0 text-muted-foreground after:absolute after:-inset-2 after:content-[''] hover:text-foreground",
        className
      )}
      {...props}
    >
      {children ?? <HugeiconsIcon icon={Cancel01Icon} strokeWidth={2} aria-hidden="true" />}
    </ToastPrimitive.Close>
  )
}

function ToastIcon({ variant }: { variant: ToastVariant }) {
  if (variant === 'default') {
    return null
  }

  let icon = null
  if (variant === 'success') {
    icon = CheckmarkCircle02Icon
  } else if (variant === 'info') {
    icon = InformationCircleIcon
  } else if (variant === 'warning') {
    icon = Alert02Icon
  } else if (variant === 'error') {
    icon = MultiplicationSignCircleIcon
  }

  if (!icon) {
    return null
  }

  return (
    <span
      data-slot="toast-icon"
      className="shrink-0 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4"
    >
      <HugeiconsIcon icon={icon} strokeWidth={2} aria-hidden="true" />
    </span>
  )
}

function ToastList() {
  const { toasts } = ToastPrimitive.useToastManager()
  const position = React.useContext(ToastPositionContext)
  const swipeDirection = swipeDirectionsForPosition(position)

  return toasts.map((toastItem) => {
    const variant = resolveToastVariant(toastItem.type)

    return (
      <Toast key={toastItem.id} toast={toastItem} variant={variant} swipeDirection={swipeDirection}>
        <ToastContent>
          <ToastIcon variant={variant} />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <ToastTitle />
            <ToastDescription />
          </div>
          <ToastAction />
          <ToastClose />
        </ToastContent>
      </Toast>
    )
  })
}

function Toaster({
  children,
  toastManager = toast,
  position = 'bottom-right',
  ...props
}: ToastPrimitive.Provider.Props & {
  position?: ToastViewportPosition
}) {
  return (
    <ToastPositionContext.Provider value={position}>
      <ToastProvider toastManager={toastManager} {...props}>
        {children}
        <ToastPortal>
          <ToastViewport position={position}>
            <ToastList />
          </ToastViewport>
        </ToastPortal>
      </ToastProvider>
    </ToastPositionContext.Provider>
  )
}

/** Card estático para documentação (sem fila/portal). */
function ToastPreview({
  className,
  variant = 'default',
  title,
  description,
}: {
  className?: string
  variant?: ToastVariant
  title: string
  description?: string
}) {
  return (
    <div
      data-slot="toast"
      data-variant={variant}
      className={cn(
        toastVariants({ variant }),
        '!relative !inset-auto w-full max-w-sm rounded-2xl shadow-sm',
        className
      )}
    >
      <div className="flex items-center gap-3 p-4">
        <ToastIcon variant={variant} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div data-slot="toast-title" className="text-sm font-medium">
            {title}
          </div>
          {description ? (
            <div data-slot="toast-description" className="text-sm">
              {description}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

const createToastManager = ToastPrimitive.createToastManager
const useToastManager = ToastPrimitive.useToastManager

export {
  createToastManager,
  Toast,
  toast,
  ToastAction,
  ToastClose,
  ToastContent,
  ToastDescription,
  Toaster,
  ToastIcon,
  ToastPortal,
  ToastPreview,
  ToastProvider,
  ToastTitle,
  toastVariants,
  ToastViewport,
  toastViewportVariants,
  useToastManager,
}
